<template>
  <tr class="cursor-pointer" :class="{'table-danger': row.status === 'error'}" @click="toggleEdit">
    <td>
      <i class="fas fa-chevron-down chevron" :class="{'chevron-open': row.is_edit}"></i>
    </td>
    <td class="fw-bold">{{ row.data.name }}</td>
    <td>{{ row.data.product_name }}</td>
    <td>{{ row.data.asset_qty }}</td>
    <td>{{ row.data.contact_profile_name }}</td>
    <td>{{ row.data.supplier_name }}</td>
    <td>{{ row.data.capitalization_date }}</td>
    <td>{{ row.data.depreciation_rate }}</td>
  </tr>

  <tr v-if="hasValidationErrors">
    <td></td>
    <td colspan="7">
      <div v-for="(messages, field) in row.validation" :key="field">
        <span v-for="(message, mIndex) in messages" :key="mIndex" class="text-danger d-block small">{{ message }}</span>
      </div>
    </td>
  </tr>

  <tr v-if="row.is_edit">
    <td colspan="8" class="edit-panel">
      <div class="d-flex justify-content-between align-items-center mb-1" @click.stop>
        <span class="fw-bold">Edit: {{ row.data.name }}</span>
        <button @click="closeEdit" class="btn btn-close" title="Close without saving"></button>
      </div>
      <div class="row g-2" @click.stop>
        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Asset Class</label>
          <AsyncSelect
              placeholder="Select Asset Class"
              v-model="assetClass"
              :api-service="fetchProductList"
              :format-label="option => option.text"
              :reduce="option => option"
              label="text"
              :additional-query="{ product_type: 'fixed_assets' }"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Asset Name</label>
          <input class="form-control" type="text" v-model="row.data.name">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Asset Qty</label>
          <input class="form-control" type="text" v-model="row.data.asset_qty">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Asset Value</label>
          <input class="form-control" type="text" v-model="row.data.asset_value">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Assign Location</label>
          <AsyncSelect
              placeholder="Select Assign Location"
              v-model="assignLocation"
              :api-service="fetchBusinessLocationsList"
              label="text"
              :format-label="option => option.text"
              :reduce="option => option"
              :additional-query="{ location_type: 'fixed_assets' }"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Assign Custodian</label>
          <AsyncSelect
              placeholder="Assign Custodian"
              v-model="custodian"
              :api-service="fetchContactProfiles"
              :format-label="formatPatientLabel"
              :reduce="option => option"
              :additional-query="{type: 'employee'}"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Department</label>
          <AsyncSelect
              placeholder="Select Department"
              v-model="department"
              :api-service="fetchDepartmentList"
              :format-label="option => option.name"
              :reduce="option => option"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Project</label>
          <AsyncSelect
              placeholder="Select Project"
              v-model="project"
              :api-service="fetchProjects"
              :format-label="option => option.name"
              :reduce="option => option"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Cost Centre</label>
          <v-select
              placeholder="Select Cost Centre"
              v-model="row.data.cost_centre_name"
              :options="costCentres"
              :reduce="name => name.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Business Unit</label>
          <v-select
              placeholder="Select Business Unit"
              v-model="row.data.business_name"
              :options="businesses"
              :reduce="name => name.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Supplier</label>
          <AsyncSelect
              placeholder="Select Supplier"
              v-model="supplier"
              :api-service="fetchContactProfiles"
              :format-label="formatPatientLabel"
              :reduce="option => option"
              :additional-query="{type: 'supplier'}"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Capitalization Date</label>
          <input class="form-control" type="date" v-model="row.data.capitalization_date">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Depreciation Rate</label>
          <input class="form-control" type="text" v-model="row.data.depreciation_rate">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Tax Depreciation Rate</label>
          <input class="form-control" type="text" v-model="row.data.tax_depreciation_rate">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Machine Id</label>
          <input class="form-control" type="text" v-model="row.data.machine_id">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Model</label>
          <input class="form-control" type="text" v-model="row.data.model">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Manufacturer</label>
          <input class="form-control" type="text" v-model="row.data.manufacturer">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Manufacturer Date</label>
          <input class="form-control" type="date" v-model="row.data.manufacturing_date">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Bill Date</label>
          <input class="form-control" type="date" v-model="row.data.bill_date">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Bill Number</label>
          <input class="form-control" type="text" v-model="row.data.bill_number">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Bill Qty</label>
          <input class="form-control" type="text" v-model="row.data.bill_qty">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Grant Donation</label>
          <select class="form-control" v-model="row.data.grant_donation">
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Grant Donation Details</label>
          <input class="form-control" type="text" v-model="row.data.grant_donation_details">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Warranty Period</label>
          <input class="form-control" type="text" v-model="row.data.warranty_period">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Insurance Details</label>
          <input class="form-control" type="text" v-model="row.data.insurance_details">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Lease Details</label>
          <input class="form-control" type="text" v-model="row.data.lease_details">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Installation Date</label>
          <input class="form-control" type="date" v-model="row.data.installation_date">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Asset Condition</label>
          <input class="form-control" type="text" v-model="row.data.asset_condition">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Useful Life</label>
          <input class="form-control" type="text" v-model="row.data.useful_life">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Residual Value</label>
          <input class="form-control" type="text" v-model="row.data.residual_value">
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <label class="col-form-label">Maintenance Schedule</label>
          <input class="form-control" type="date" v-model="row.data.maintenance_schedule">
        </div>

        <div class="col-12">
          <label class="col-form-label">Asset Description</label>
          <textarea class="form-control" v-model="row.data.asset_description" rows="2"></textarea>
        </div>
      </div>

      <div class="d-flex justify-content-end pt-1">
        <button @click.stop="onClickUpdate" class="btn btn-success btn-sm">Update</button>
      </div>
    </td>
  </tr>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.chevron {
  color: #6c757d;
  transition: transform 0.15s ease-in-out;
}
.chevron-open {
  transform: rotate(-180deg);
}
.edit-panel {
  background-color: #f8f9fb;
  padding: 10px 12px !important;
}
.edit-panel :deep(.col-form-label) {
  padding: 0 0 2px;
  font-size: 12px;
  margin-bottom: 0;
}
.edit-panel :deep(.row) {
  --bs-gutter-y: 8px;
}
</style>

<script setup>
import {ref, computed, watch} from 'vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchaseAndSales from '@/services/modules/purchase'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleContact from '@/services/modules/contact'
import handleDepartments from '@/services/modules/procurement/organization-settings/department'
import handleProjects from '@/services/modules/procurement/project'
import {useAsyncDropdownHelper} from '@/services/utils/asyncDropdownHelper'

const props = defineProps({
  row: {type: Object, required: true},
  costCentres: {type: Array, default: () => []},
  businesses: {type: Array, default: () => []}
})

const emit = defineEmits(['onClickUpdate'])

const hasValidationErrors = computed(() => Object.keys(props.row.validation || {}).length > 0)

const {fetchProductList} = handlePurchaseAndSales()
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const {fetchContactProfiles} = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()
const {fetchDepartmentList} = handleDepartments()
const {fetchProjects} = handleProjects()

const assetClass = ref({id: props.row.data.product_id, text: props.row.data.product_name})
const assignLocation = ref({id: props.row.data.business_location_id, text: props.row.data.business_location_name})
const custodian = ref({id: props.row.data.contact_profile_id, name: props.row.data.contact_profile_name})
const department = ref({id: props.row.data.department_id, name: props.row.data.department_name})
const project = ref({id: props.row.data.project_master_id, name: props.row.data.project_master_name})
const supplier = ref({id: props.row.data.supplier_id, name: props.row.data.supplier_name})

watch(assetClass, (value) => {
  if (!value) {
    props.row.data.product_id = null
    props.row.data.product_name = null
    return
  }
  props.row.data.product_id = value.id
  props.row.data.product_name = value.text
})

watch(assignLocation, (value) => {
  if (!value) {
    props.row.data.business_location_id = null
    props.row.data.business_location_name = null
    return
  }
  props.row.data.business_location_id = value.id
  props.row.data.business_location_name = value.text
})

watch(custodian, (value) => {
  if (!value) {
    props.row.data.contact_profile_id = null
    props.row.data.contact_profile_name = null
    return
  }
  props.row.data.contact_profile_id = value.id
  props.row.data.contact_profile_name = value.name
})

watch(department, (value) => {
  if (!value) {
    props.row.data.department_id = null
    props.row.data.department_name = null
    return
  }
  props.row.data.department_id = value.id
  props.row.data.department_name = value.name
})

watch(project, (value) => {
  if (!value) {
    props.row.data.project_master_id = null
    props.row.data.project_master_name = null
    return
  }
  props.row.data.project_master_id = value.id
  props.row.data.project_master_name = value.name
})

watch(supplier, (value) => {
  if (!value) {
    props.row.data.supplier_id = null
    props.row.data.supplier_name = null
    return
  }
  props.row.data.supplier_id = value.id
  props.row.data.supplier_name = value.name
})

function toggleEdit() {
  props.row.is_edit = !props.row.is_edit
}

function closeEdit() {
  props.row.is_edit = false
}

function onClickUpdate() {
  emit('onClickUpdate', props.row)
}
</script>
